import './styles/TextEditor.scss';

import isHotkey from 'is-hotkey';
import {
  FocusEvent,
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createEditor } from 'slate';
import { Editable, Slate, withReact } from 'slate-react';

import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks';
import { Dropdown } from '../Dropdown';
import { InputField } from '../InputField';
import {
  BlockButton,
  Element,
  Leaf,
  MarkButton,
  toggleMark,
  Toolbar,
} from './components/EditorElements';

const componentName = 'c-text-editor';

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
} as const;

export const EMPTY_TEXT = [{ type: 'paragraph', children: [{ text: '' }] }];

const parseEmptyValue = (value: any[]) => {
  if (value && Array.isArray(value)) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
  return [{ type: 'paragraph', children: [{ text: value }] }];
};

const getDefaultValue = (value: any[]) => {
  return value && Array.isArray(value) && value.length > 0
    ? value
    : parseEmptyValue(value);
};

export interface TextEditorProps extends PropsWithChildren {
  name?: string;
  value: any;
  viewOnly?: boolean;
  onBlur?: (event: FocusEvent<HTMLDivElement, Element>) => void;
  fixed?: boolean;
  valueFontParagraph?: string;
  onFocus?: () => void;
  onChange?: (
    e: React.ChangeEvent<{ value: any[]; name?: string; id?: string }>,
  ) => void;
  error?: boolean;
  label?: string;
  required?: boolean;
  errorMessage?: string;
  placeholder?: string;
}

// TODO: React.TextareaHTMLAttributes<HTMLDivElement> This prop should extend from Textarea and need to be refactored to avoid custom handleOnChange and try to use the native event
export interface EditorProps {
  value: any[];
  name: string;
  onChange?: (
    e: React.ChangeEvent<{ value: any[]; name?: string; id?: string }>,
  ) => void;
  onBlur?: (event: FocusEvent<HTMLDivElement, Element>) => void;
  onFocus?: () => void;
  fixed?: boolean;
  valueFontParagraph?: string;
  isError?: boolean;
  id?: string;
  placeholder?: string;
}

const Editor = ({
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  fixed,
  valueFontParagraph,
  isError,
  id,
  placeholder,
}: EditorProps) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  const [internalValue, setInternalValue] = useState(() =>
    getDefaultValue(value),
  );
  const c = useClassHelper(componentName);

  useEffect(() => {
    const newValue = getDefaultValue(value);
    if (JSON.stringify(newValue) !== JSON.stringify(internalValue)) {
      setInternalValue(newValue);
      // Slate reads `initialValue` only at mount — the document lives in
      // editor.children afterwards, so an external value change (e.g. the
      // wizard's "copy content" patching formik) must reset it by hand.
      // The selection is cleared first: it may point into nodes that no
      // longer exist in the new document and would crash the next render.
      editor.selection = null;
      editor.children = newValue;
      editor.onChange();
    }
  }, [value]);

  const renderElement = useCallback(props => <Element {...props} />, []);
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);

  const handleEditorChange = (newValue: any[]) => {
    setInternalValue(newValue);
    if (
      onChange &&
      JSON.stringify(newValue) !== JSON.stringify(internalValue)
    ) {
      const fakeEvent = {
        target: { value: newValue, name, id: name },
        currentTarget: { value: newValue, name, id: name },
      } as unknown as React.ChangeEvent<{
        value: any[];
        name?: string;
        id?: string;
      }>;

      onChange(fakeEvent);
    }
  };

  const handleKeydown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    for (const [hotkey, mark] of Object.entries(HOTKEYS)) {
      if (isHotkey(hotkey, event)) {
        event.preventDefault();
        toggleMark(editor, mark);
      }
    }
  };

  return (
    <Slate
      editor={editor}
      initialValue={internalValue}
      onChange={handleEditorChange}
    >
      <Toolbar className={isError ? 'error' : ''}>
        <MarkButton format="bold" />
        <MarkButton format="italic" />
        <MarkButton format="underline" />
        <MarkButton format="code" />
        <BlockButton format="heading-one" />
        <BlockButton format="heading-two" />
        <BlockButton format="heading-three" />
        <BlockButton format="block-quote" />
        <BlockButton format="numbered-list" />
        <BlockButton format="bulleted-list" />
        <BlockButton format="left" />
        <BlockButton format="center" />
        <BlockButton format="right" />
        <BlockButton format="justify" />
      </Toolbar>
      <Dropdown.GoogleFont.Viewer fontFamily={valueFontParagraph}>
        <div
          className={`${c('__editor', {
            __editor__fixed: fixed,
            '__editor--error': isError,
          })} ${cn('border border-gray-300 focus-within:border-blue-700')}`}
        >
          <Editable
            name={name}
            id={id}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={handleKeydown}
            onFocus={onFocus}
            role="textbox"
            onBlur={onBlur}
            tabIndex={0}
            z-index={1}
            aria-label={id}
            placeholder={placeholder}
            // Slate computes an inline min-height from the placeholder height,
            // which beats any stylesheet rule. Its Editable spreads this style
            // prop last, so the default height must be set here, not in scss.
            style={{ minHeight: '110px' }}
          />
        </div>
      </Dropdown.GoogleFont.Viewer>
    </Slate>
  );
};

export interface TextViewerProps {
  value: any[];
  fixed?: boolean;
  valueFontParagraph?: string;
}

const TextViewer = ({ value, fixed, valueFontParagraph }: TextViewerProps) => {
  const editor = useMemo(() => withReact(createEditor()), [value]);
  const c = useClassHelper(componentName);

  return (
    <Slate
      key={JSON.stringify(value)}
      editor={editor}
      initialValue={getDefaultValue(value)}
      onChange={() => undefined}
    >
      <Dropdown.GoogleFont.Viewer fontFamily={valueFontParagraph}>
        <div
          role="textbox"
          className={c('__editor', '__view', {
            __editor__fixed: fixed,
          })}
        >
          <Editable
            renderElement={props => <Element {...props} />}
            renderLeaf={props => <Leaf {...props} />}
            readOnly
            className="wrap-anywhere! rich-editor"
          />
        </div>
      </Dropdown.GoogleFont.Viewer>
    </Slate>
  );
};

export const TextEditor = (props: TextEditorProps) => {
  const {
    viewOnly = false,
    value,
    fixed,
    valueFontParagraph,
    name = '',
    required,
    label,
    error,
    errorMessage,
  } = props;

  if (viewOnly) {
    return (
      <Fragment>
        {label && (
          <InputField.Label id={name} required={required} aria-hidden>
            {label}
          </InputField.Label>
        )}
        <TextViewer
          value={value}
          fixed={fixed}
          valueFontParagraph={valueFontParagraph}
        />
        <InputField.ErrorMessage
          id={name}
          error={error}
          errorMessage={errorMessage}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      {label && (
        <InputField.Label id={name} required={required} aria-hidden>
          {label}
        </InputField.Label>
      )}
      <Editor {...props} isError={Boolean(error)} id={name} name={name} />
      <InputField.ErrorMessage
        id={name}
        error={error}
        errorMessage={errorMessage}
      />
    </Fragment>
  );
};

// Static method to create custom validation schema for the editor's content
TextEditor.CustomSchemaValidationFactory = yupSchema => {
  return yupSchema.test(
    'yup-mixed-required',
    'yup.mixed.required',
    (value?: any[]) => {
      if (!Array.isArray(value) || value.length === 0) {
        return false;
      }
      return value.some(block => {
        return (
          Array.isArray(block.children) &&
          block.children.some(child => {
            return (
              (typeof child.text === 'string' && child.text.trim() !== '') ||
              (Array.isArray(child.children) && child.children.length > 0)
            );
          })
        );
      });
    },
  );
};
